import {
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    LegacyCard,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    TextContainer,
    List,
    LegacyStack,
    Select,
    Text,
    DropZone,
    BlockStack,
    InlineStack,
    Divider,
    InlineGrid, 
    Checkbox,
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep } from 'lodash';
import { useAppBridge, useToast } from "@shopify/app-bridge-react";
import { Redirect } from '@shopify/app-bridge/actions';
import { useLocation, useParams } from 'react-router-dom';
import Api from '../../helpers/api';
import CustomerOnHold from "../customers/CustomerOnHold";
import {getSessionToken} from "@shopify/app-bridge-utils";
import jwtDecode from "jwt-decode";
import * as helpers from './helpers';
import {CollectionsSelector} from "./CollectionsSelector";
import {DatePickerSelector, ModalTextInput} from "../Selectors";
import {toDate} from "../Dates";
import {LineItemEngraving, LineItemEngravingDescription} from "./Engavings";

const AddressDetails = ({address}) => {
    const parts = [
        `${address?.recipient || ''}`,
        `${address?.address1 || ''}`,
        `${address?.address2 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Defined)</span>)
}

const PriceMarker = ({ price, strikethrough }) => {
    const style = strikethrough
        ? { textDecoration: 'line-through' }
        : {};

    return (
        <Text variant="headingMd" as="h2">
            <span style={style}>${price}</span>
        </Text>
    );
}

const FinalPrice = ({ lineItem, children }) => {
    const [showInput, setShowInput] = useState(false);
    const onClick = useCallback(() => {
        if (! lineItem.price.locked) {
            setShowInput(true);
        }
    }, []);

    let discounted = lineItem.price.discounted || '0.00';

    if (lineItem.price.discount && lineItem.price.discount.quantity > 0 && discounted > 0) {
        let totalDiscount = lineItem.price.discount.total;
        discounted = discounted - parseFloat((totalDiscount / lineItem.price.discount.quantity).toFixed(2));
    }

    const prices = Array
        .from(new Set([
            parseFloat(lineItem.price.unit || '0.00').toFixed(2),
            parseFloat(discounted).toFixed(2),
        ]))
        .map((price, i, list) => ({
            price,
            strikethrough: i == 0 && list.length > 1,
        }));

    if (! showInput) {
        const style = lineItem.price.locked
            ? {}
            : { cursor: 'pointer' };

        return (
            <div onClick={onClick} title="Enter custom price" style={style}>
                {prices.map((props, i) => <PriceMarker {...props} key={i} />)}
            </div>
        );
    }

    return (
        <>
            {children}
        </>
    );
}

const Orders = () => {
    const toast = useToast();
    const search = useLocation().search;
    const app = useAppBridge();
    const redirect = Redirect.create(app);
    const {customerid} = useParams();
    const [locationId] = useState(
        (new URLSearchParams(search)).get('location_id') || null
    );
    const [dropshipKey] = useState(
        (new URLSearchParams(search)).get('dropship') || null
    );
    const [dropshipAddress, setDropshipAddress] = useState(null);

    const [adminUserId, setAdminUserId] = useState(null);

    const [customer, setCustomer] = useState(null);
    const [location, setLocation] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [additionalInfo, setAdditionalInfo] = useState({
        placed_by: '',
        po_number: '',
        order_notes: '',
        customer_notes: '',
        ship_date: null,
        cancel_date: null,
        ship_complete: false,
    });
    const [searchTags, setSearchTags] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setSearching] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedCollection, setCollection] = useState([]);
    const [activeLineItem, setActiveLineItem] = useState(null);
    const [activeLineItemDetails, setActiveLineItemDetails] = useState(null);

    // Mobile swap for the products table
    const window_mobile_threshold = 640;
    const [isMobile, setIsMobile] = useState(window.innerWidth < window_mobile_threshold);

    const updateQuantity = useCallback((key, quantity) => {
        let clone = cloneDeep(lineItems);

        quantity = parseInt(quantity);

        if (quantity == '') {
            quantity = 0;
        }

        if (quantity == 0) {
            clone[key].file = null;
            removeEngravingProperties(key);
        }

        clone[key].quantity = quantity.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);
    }, [lineItems, location]);

    const decreaseQuantity = key => {
        const clone = cloneDeep(lineItems);
        const current = parseInt(clone[key].quantity) - 1;

        if ( current < 0 ) return;

        updateQuantity(key, current);
    }

    const increaseQuantity = key => {
        const clone = cloneDeep(lineItems);
        const current = parseInt(clone[key].quantity) + 1;
        updateQuantity(key, current);
    }

    const updateManualPrice = useCallback((key, price) => {
        let clone = cloneDeep(lineItems);

        if (price < 0) {
            price = 0;
        } else if (parseFloat(price) > parseFloat(clone[key].price.unit)) {
            price = clone[key].price.unit;
        }

        clone[key].price.manual = price.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);
    }, [lineItems, location]);

    const updateNotes = useCallback((key, notes) => {
        let clone = cloneDeep(lineItems);

        clone[key].notes = notes;

        setLineItems(clone);
    }, [lineItems, location]);

    // Getting the customer from the router parameter.
    useEffect(() => {
        setLoading(true);
        getSessionToken(app).then(sessionToken => {
            const decodedToken = jwtDecode(sessionToken);

            if (decodedToken['sub']) {
                const id = decodedToken['sub'];
                setAdminUserId(id);
            }
        });
        Promise
            .all([
                Api.getCustomer(customerid).then(item => item.data),
                Api.getLocation(locationId).then(item => item.data),
            ])
            .then(([customerData, locationData]) => {
                setCustomer(customerData.data);
                setLocation(locationData.data);
                setAdditionalInfo({
                    ...additionalInfo,
                    customer_notes: locationData.data.notes,
                });
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect( () => {
        if ( dropshipKey ) {
            Api.getDropshipAddress(dropshipKey).then( ({data}) => {
                setDropshipAddress(data.address);
                console.log(data.address);
            });
        }
    }, [dropshipKey])

    const searchProducts = useCallback(() => {
        setSearching(true);
        Api.getProducts(searchTags, searchInput, locationId, selectedCollection)
            .then(({ data }) => {
                const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

                setHasMoreResults(data.meta.last_page > 1);

                const updatedLineItems = data.data.filter(item => ! selectedLineItems.some(({ id }) => id == item.shopify_variant_id))
                    .map(variant => ({
                        ...variant,

                        id: variant.shopify_variant_id,
                        quantity: "0",
                        title: `${variant.sku} - ${variant.product_title}`,
                        price: {
                            unit: variant.price,
                            discounted: variant.price_list
                                ? variant.price
                                : (parseFloat(variant.price) - (parseFloat(variant.price) * (customer?.discount ?? 0) / 100))
                                    .toFixed(2),
                            total: 0,
                            manual: null,
                            locked: variant.price_list,
                            price_list: variant.price_list,
                            original: variant.original_price,
                        },
                        file: null,
                        engravingProperties: [],
                        notes: '',
                    })).concat(selectedLineItems);

                setLineItems(updatedLineItems);
            })
            .catch(() => {
                toast.show("Error while searching products. Please try again.");
            })
            .finally(() => setSearching(false));
    }, [lineItems, searchTags, searchInput, locationId, selectedCollection]);

    const placeDraftOrder = useCallback(async () => {
        const submitErrors = {};
        const variants = lineItems
            .filter(item => item.quantity > 0)
            .flatMap(item => {
                return [{
                    ...item,
                    custom_attributes: [
                        { key: '_uploaded_file', value: item.file?.url ?? '' },
                        { key: '_custom_notes', value: item.notes },
                        ...item.engravingProperties.map(property => {
                            if (property.key == 'Preview') {
                                return {
                                    ...property,
                                    value: property.value.url,
                                }
                            }

                            return property;
                        }),
                    ],
                }];
            })
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                applied_discount: helpers.getDiscountDetails(item),
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                discounted_price: parseFloat(item.price.total),
                original_price: parseFloat(item.price.original),
            }));

        const draftOrderData = {
            variants,
            location_id: location.id,
            total_price: lineItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.total);
            }, 0.0),
            placed_by: additionalInfo.placed_by,
            account_number: customer?.account_number || null,
            po_number: additionalInfo.po_number,
            customer_notes: location.notes,
            order_notes: additionalInfo.order_notes,
            cancel_date: additionalInfo.cancel_date,
            ship_date: additionalInfo.ship_date,
            billing_address: helpers.formatLocationAddress(location?.billing ?? customer?.default_location?.billing, customer, location?.phone),
            shipping_address: helpers.formatLocationAddress(location?.shipping, customer, location?.phone),
            user_id: adminUserId,
            dropship_key: dropshipKey,
            ship_complete: additionalInfo.ship_complete,
        };

        try {
            let {data} = await Api.placeDraftOrder( draftOrderData );

            return data.data;
        } catch ( error ) {
            if ( error.response.status == 422 ) {
                Object.keys(error.response.data.errors).forEach( key => {
                    submitErrors[key] = error.response.data.errors[key][0];
                })
                setErrors( submitErrors );
            } else {
                setCreating(false);
                throw error;
            }
        }

        return null;
    }, [lineItems, location, additionalInfo, adminUserId, customer, dropshipKey]);

    const placeAndReview = async () => {
        setCreating(true);

        const draft = await placeDraftOrder();

        if (draft) {
            setTimeout(() => {
                redirect.dispatch(Redirect.Action.ADMIN_PATH, `/draft_orders/${draft.id}`);
                setCreating(false);
            }, 5000);

            return;
        }

        setCreating(false);
    }

    const changeAdditionalInfo = useCallback((value, id) => {
        setAdditionalInfo({
            ...additionalInfo,
            [id]: value,
        });
    }, [additionalInfo]);

    const handleDropZoneDrop = useCallback((lineItemIndex, file) => {
        setUploading(true);

        Api
            .uploadFileForCart(file)
            .then(({ data }) => {
                toast.show("File has been uploaded");
                changeFileForLineItem(lineItemIndex, data);
            })
            .catch((error) => {
                toast.show("Error while uploading file. Please try again.", {isError: true});
            })
            .finally(() => {
                setUploading(false);
            })
    }, [lineItems]);

    const deleteFile = useCallback((lineItemIndex, file) => {
        setUploading(true);

        Api
            .deleteFileForCart(file)
            .then(({ data }) => {
                changeFileForLineItem(lineItemIndex, null);
            })
            .catch(() => {
                toast.show("Error while deleting file. Please try again.", {isError: true});
            })
            .finally(() => {
                setUploading(false);
            })
    }, [lineItems]);

    const changeFileForLineItem = useCallback((lineItemIndex, file) => {
        let clone = cloneDeep(lineItems);

        clone[lineItemIndex].file = file;

        setLineItems(clone);
    }, [lineItems]);

    const updateEngravingProperties = useCallback((key, properties) => {
        let clone = cloneDeep(lineItems);
        clone[key].engravingProperties = Object.entries(properties)
            .map(([key, value]) => {
                return { key: key, value: value };
            });
        setLineItems(clone);
    }, [lineItems]);

    const removeEngravingProperties = useCallback(async (key) => {
        let clone = cloneDeep(lineItems);
        let properties = clone[key].engravingProperties;

        if (properties['Preview'] && properties['Preview'].path) {
            await Api.deleteFileForCart(properties['Preview'].path);
        }

        clone[key].engravingProperties = [];
        setLineItems(clone);
    }, [lineItems]);

    // Update for mobile screens
    useEffect(()=>{
        setIsMobile(window.innerWidth < window_mobile_threshold);

        const updateIsMobile = () => {
            setIsMobile(window.innerWidth < window_mobile_threshold);
        };

        window.addEventListener("resize", updateIsMobile);

        return () => window.removeEventListener("resize", updateIsMobile)
    }, [])

    return (
        <Frame>
            <Page title={`Place Order`}>
                <Layout>
                    <CustomerOnHold customer={customer}/>

                    <Layout.Section>
                    <LegacyCard title={`Company Account`} sectioned>
                        <DescriptionList items={[
                            {term: 'Account Number', description: customer?.account_number ?? '' },
                            {term: 'Email', description: customer?.email },
                            {term: 'Phone', description: customer?.phone_number },
                            {term: 'Name', description: `${customer?.additional_data.first_name} ${customer?.additional_data.last_name}` },
                            {term: 'Discount', description: `${location?.discount || 0}%` },
                            {term: 'Shipping', description: dropshipAddress ? <AddressDetails address={dropshipAddress} /> : <AddressDetails address={location?.shipping} /> },
                            {term: 'Billing', description: <AddressDetails address={location?.billing} /> },
                        ]} />
                        <LegacyCard.Section>
                            <FormLayout>
                                <TextField autoComplete={'off'} disabled={loading} requiredIndicator={true} label="Who is the person who is placing this order for the Company?" id="placed_by" value={additionalInfo.placed_by} onChange={changeAdditionalInfo} />
                                <TextField autoComplete={'off'} disabled={loading} label="PO Number" id="po_number" value={additionalInfo.po_number} onChange={changeAdditionalInfo} />
                                <DatePickerSelector
                                    modalTitle={"Select Ship Date"}
                                    label={"Ship Date"}
                                    startDate={additionalInfo.ship_date}
                                    allowRange={false}
                                    multiMonth={false}
                                    disabled={loading}
                                    onChange={(date) => changeAdditionalInfo(toDate(date, false), 'ship_date')}
                                />
                                <DatePickerSelector
                                    onChange={(date) => changeAdditionalInfo(toDate(date, false), 'cancel_date')}
                                    modalTitle={"Select Cancel Date"}
                                    label={"Cancel Date"}
                                    startDate={additionalInfo.cancel_date}
                                    allowRange={false}
                                    multiMonth={false}
                                    disabled={loading}
                                />
                            </FormLayout>
                            </LegacyCard.Section>
                    </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard title="Line Items">
                            <LegacyCard.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                        <CollectionsSelector loading={loading} selected={selectedCollection} setSelection={setCollection} />
                                        <TextField disabled={loading} autoComplete={'off'} label="Tags" value={searchTags} onChange={setSearchTags} />
                                        <TextField disabled={loading} autoComplete={'off'} label="Search" value={searchInput} onChange={setSearchInput} />
                                        <Button submit loading={isSearching || loading} disabled={activeLineItem} variant="primary">Search</Button>
                                    </FormLayout>
                                </Form>
                            </LegacyCard.Section>
                            {lineItems.length > 0 && (
                                <>
                                    <LegacyCard.Section title="Line Items">
                                        <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                            <thead>
                                                <tr>
                                                    <th className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left'}}>Item</th>
                                                    {!isMobile && (<>
                                                        <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '100px' }}>File</th>
                                                        <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '100px' }}>Quantity</th>
                                                        <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '100px' }}>Unit Price</th>
                                                        <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '80px' }}>Final Price</th>
                                                    </>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {lineItems.map( (line_item, key) => {
                                                return isMobile
                                                    ? (
                                                        <tr key={line_item.id}>
                                                            <td className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left', paddingBottom: isMobile ? 'var(--p-space-500)' : null}}>
                                                                <LegacyStack vertical>
                                                                    <LegacyStack horizontal alignment="center" wrap={false}>
                                                                        <Thumbnail alt={''} source={line_item.image_url} />
                                                                        <LegacyStack.Item fill>
                                                                            <div style={{whiteSpace: 'normal'}}>
                                                                                <Text variant="headingMd" as="h2" >{line_item.title}</Text>
                                                                            </div>
                                                                            {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                            {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}

                                                                            <InlineStack gap={'200'}>
                                                                                {line_item.quantity > 0 && (<ModalTextInput
                                                                                    value={line_item.notes}
                                                                                    onChange={(value) => updateNotes(key, value)}
                                                                                    disabled={uploading || loading}
                                                                                />)}

                                                                                {line_item.hasEngravings && line_item.quantity > 0 && (<>
                                                                                    {Object.entries(line_item.engravingProperties).length == 0 && (
                                                                                        <Button
                                                                                            onClick={() => setActiveLineItem({key: key, line_item: line_item})}
                                                                                        >Engravings</Button>
                                                                                    )}
                                                                                    {Object.entries(line_item.engravingProperties).length > 0 && (
                                                                                        <Button
                                                                                            onClick={() => setActiveLineItemDetails(line_item)}
                                                                                        >Show Engravings</Button>
                                                                                    )}
                                                                                    {Object.entries(line_item.engravingProperties).length > 0 && (
                                                                                        <Button
                                                                                            variant={'primary'}
                                                                                            tone={'critical'}
                                                                                            onClick={() => removeEngravingProperties(key)}
                                                                                        >Remove Engravings</Button>
                                                                                    )}
                                                                                </>)}
                                                                            </InlineStack>

                                                                        </LegacyStack.Item>
                                                                    </LegacyStack>
                                                                    {line_item.quantity > 0 && (
                                                                        <InlineStack alignment="center" wrap={false} gap={200}>
                                                                            {!!line_item.file && (<>
                                                                                <Button
                                                                                    size={"slim"}
                                                                                    variant={"primary"}
                                                                                    url={line_item.file.url}
                                                                                    external={true}
                                                                                    disabled={uploading || loading}
                                                                                >View File</Button>
                                                                                <Button
                                                                                    size={"slim"}
                                                                                    variant={"primary"}
                                                                                    tone={"critical"}
                                                                                    disabled={uploading || loading}
                                                                                    onClick={() => deleteFile(key, line_item.file.path)}
                                                                                >Remove File</Button>
                                                                            </>)}

                                                                            {!line_item.file && (
                                                                                <LegacyStack.Item fill>
                                                                                    <DropZone
                                                                                        disabled={uploading || loading}
                                                                                        allowMultiple={false}
                                                                                        onDrop={(_dropFiles, acceptedFiles, _rejectedFiles) => {
                                                                                            handleDropZoneDrop(key, acceptedFiles[0]);
                                                                                        }}
                                                                                    >
                                                                                        <DropZone.FileUpload />
                                                                                    </DropZone>
                                                                                </LegacyStack.Item>
                                                                            )}
                                                                        </InlineStack>
                                                                    )}

                                                                    <InlineGrid columns={2} gap={400}>
                                                                    <TextField label="Quantity" autoComplete={'off'} disabled={loading} value={line_item.quantity}
                                                                                min={0}
                                                                                step={line_item.increment_quantity_by ?? 1}
                                                                                type="number"
                                                                                onChange={quantity => updateQuantity(key, quantity)}
                                                                                connectedLeft={<Button disabled={line_item.quantity < 1} onClick={() => decreaseQuantity(key)}>&minus;</Button>}
                                                                                connectedRight={<Button onClick={() => increaseQuantity(key)}>+</Button>}
                                                                            />
                                                                        <InlineGrid columns={2} gap={200}>
                                                                            <div>
                                                                                <div style={{marginBottom: '10px'}}><Text variant="headingXs" as="h3">Unit Price</Text></div>
                                                                                <FinalPrice lineItem={line_item}>
                                                                                    <TextField autoComplete={'off'} disabled={loading} type="number"
                                                                                            prefix="$"
                                                                                            value={(line_item.price.manual === null) ? line_item.price.discounted : line_item.price.manual}
                                                                                            min="0"
                                                                                            max={line_item.price.unit}
                                                                                            onChange={price => updateManualPrice(key, price)}/>
                                                                                </FinalPrice>
                                                                            </div>
                                                                            <div>
                                                                                <div style={{marginBottom: '10px'}}><Text variant="headingXs" as="h3">Final Price</Text></div>
                                                                                <Text variant="headingMd" as="div">${(parseFloat(line_item.price.total) - parseFloat(line_item?.price?.discount?.total ?? 0)).toFixed(2)}</Text>
                                                                            </div>
                                                                        </InlineGrid>
                                                                    </InlineGrid>

                                                                    <LegacyStack vertical spacing="loose">
                                                                        <Divider/>
                                                                    </LegacyStack>

                                                                </LegacyStack>
                                                            </td>
                                                        </tr>
                                                    )
                                                    :(
                                                        <tr key={line_item.id}>
                                                            <td className="Polaris-DataTable__Cell"><Thumbnail alt={''} source={line_item.image_url} /></td>
                                                            <td className="Polaris-DataTable__Cell" style={{whiteSpace: 'normal'}}>
                                                                <div>
                                                                    {line_item.title}
                                                                    {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                    {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                                </div>
                                                                <InlineStack gap={'200'}>
                                                                    {line_item.quantity > 0 && (<ModalTextInput
                                                                        value={line_item.notes}
                                                                        onChange={(value) => updateNotes(key, value)}
                                                                        disabled={uploading || loading}
                                                                    />)}

                                                                    {line_item.hasEngravings && line_item.quantity > 0 && (<>
                                                                        {Object.entries(line_item.engravingProperties).length == 0 && (
                                                                            <Button
                                                                                onClick={() => setActiveLineItem({key: key, line_item: line_item})}
                                                                            >Engravings</Button>
                                                                        )}
                                                                        {Object.entries(line_item.engravingProperties).length > 0 && (
                                                                            <Button
                                                                                onClick={() => setActiveLineItemDetails(line_item)}
                                                                            >Show Engravings</Button>
                                                                        )}
                                                                        {Object.entries(line_item.engravingProperties).length > 0 && (
                                                                            <Button
                                                                                variant={'primary'}
                                                                                tone={'critical'}
                                                                                onClick={() => removeEngravingProperties(key)}
                                                                            >Remove Engravings</Button>
                                                                        )}
                                                                    </>)}
                                                                </InlineStack>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                {line_item.quantity > 0 && !!line_item.file && (<BlockStack gap={200}>
                                                                    <Button
                                                                        size={"slim"}
                                                                        variant={"primary"}
                                                                        url={line_item.file.url}
                                                                        external={true}
                                                                        disabled={uploading || loading}
                                                                    >View File</Button>
                                                                    <Button
                                                                        size={"slim"}
                                                                        variant={"primary"}
                                                                        tone={"critical"}
                                                                        disabled={uploading || loading}
                                                                        onClick={() => deleteFile(key, line_item.file.path)}
                                                                    >Remove File</Button>
                                                                </BlockStack>)}

                                                                {line_item.quantity > 0 && !line_item.file && (
                                                                    <DropZone
                                                                        disabled={uploading || loading}
                                                                        allowMultiple={false}
                                                                        onDrop={(_dropFiles, acceptedFiles, _rejectedFiles) => {
                                                                            handleDropZoneDrop(key, acceptedFiles[0]);
                                                                        }}
                                                                    >
                                                                        <DropZone.FileUpload />
                                                                    </DropZone>
                                                                )}
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <TextField autoComplete={'off'} disabled={loading} value={line_item.quantity}
                                                                        min={0}
                                                                        step={line_item.increment_quantity_by ?? 1}
                                                                        type="number"
                                                                        onChange={quantity => updateQuantity(key, quantity)}
                                                                />
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <FinalPrice lineItem={line_item}>
                                                                    <TextField autoComplete={'off'} disabled={loading} type="number"
                                                                            prefix="$"
                                                                            value={(line_item.price.manual === null) ? line_item.price.discounted : line_item.price.manual}
                                                                            min="0"
                                                                            max={line_item.price.unit}
                                                                            onChange={price => updateManualPrice(key, price)}/>
                                                                </FinalPrice>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">${(parseFloat(line_item.price.total) - parseFloat(line_item?.price?.discount?.total ?? 0)).toFixed(2)}</Text>
                                                            </td>
                                                        </tr>
                                                    )
                                            })}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    {isMobile
                                                        ? (
                                                            <td className="Polaris-DataTable__Cell" colSpan="2">
                                                                <LegacyStack horizontal wrap={false}>
                                                                    <LegacyStack.Item fill>
                                                                        <Text variant="headingMd" as="h2">Totals</Text>
                                                                    </LegacyStack.Item>
                                                                    <BlockStack>
                                                                        <Text variant="headingXs" as="h3">Quantity</Text>
                                                                        <Text variant="headingMd" as="div">
                                                                            {lineItems.reduce((acc, item) => {
                                                                                    acc += parseInt(item.quantity) ;
                                                                                    return acc;
                                                                                }, 0)}
                                                                        </Text>
                                                                    </BlockStack>
                                                                    <BlockStack>
                                                                        <Text variant="headingXs" as="h3">Price</Text>
                                                                        <Text variant="headingMd" as="div">
                                                                            {`$${ lineItems.reduce((acc, item) => {
                                                                                return acc + parseFloat(item.price.total) - parseFloat(item?.price?.discount?.total ?? 0);
                                                                            }, 0.0).toFixed(2)}`}
                                                                        </Text>
                                                                    </BlockStack>
                                                                </LegacyStack>
                                                            </td>
                                                        )
                                                        : (
                                                            <>
                                                                <td className="Polaris-DataTable__Cell" colSpan="3"><Text variant="headingMd" as="h2">Totals</Text></td>
                                                                <td className="Polaris-DataTable__Cell">
                                                                    <Text variant="headingMd" as="h2">
                                                                        {lineItems.reduce((acc, item) => {
                                                                                acc += parseInt(item.quantity) ;
                                                                                return acc;
                                                                            }, 0)}
                                                                    </Text>
                                                                </td>
                                                                <td className="Polaris-DataTable__Cell">&nbsp;</td>
                                                                <td className="Polaris-DataTable__Cell">
                                                                    <Text variant="headingMd" as="h2">
                                                                        {`$${ lineItems.reduce((acc, item) => {
                                                                            return acc + parseFloat(item.price.total) - parseFloat(item?.price?.discount?.total ?? 0);
                                                                        }, 0.0).toFixed(2)}`}
                                                                    </Text>
                                                                </td>
                                                            </>
                                                        )
                                                    }
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </LegacyCard.Section>
                                </>
                            )}
                            {hasMoreResults && (
                                <LegacyCard.Section>
                                    <Banner tone="info">
                                        <p>There are more matching products. Please narrow down the search query.</p>
                                    </Banner>
                                </LegacyCard.Section>
                            )}
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned title="Additional Information">
                            <FormLayout>
                                <Checkbox label={"Ship Complete"} checked={additionalInfo.ship_complete} onChange={changeAdditionalInfo} id="ship_complete" />
                                <TextField autoComplete={'off'} disabled={loading} multiline={3} label="Order Notes" value={additionalInfo.order_notes} id="order_notes" onChange={changeAdditionalInfo} />
                            </FormLayout>
                        </LegacyCard>
                    </Layout.Section>
                    { customer?.cards == null && (
                        <Layout.Section>
                        <Banner
                            title="No credit card added to this Company"
                            tone="warning"
                            >
                            <TextContainer>
                                <p>The Company you are placing an order for doesn't have a credit card added to his profile. Please add a credit card first.</p>
                            </TextContainer>
                            </Banner>
                        </Layout.Section>
                    )}
                    { Object.keys(errors).length > 0 && (
                        <Layout.Section>
                        <Banner
                            title="There were some errors creating the order."
                            tone="critical"
                            >
                                <List>
                                    {Object.keys(errors).map(key => <List.Item><strong>{key.replace('_', ' ')}</strong>: {errors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        </Layout.Section>
                    )}

                    <CustomerOnHold customer={customer}/>
                    <LineItemEngraving
                        modalActive={activeLineItem}
                        closeModal={() => setActiveLineItem(null)}
                        onSave={(value) => {
                            if (activeLineItem) {
                                updateEngravingProperties(activeLineItem.key, value);
                            }
                            setActiveLineItem(null);
                        }}
                        product={activeLineItem?.line_item}
                    />
                    <LineItemEngravingDescription
                        modalActive={activeLineItemDetails}
                        closeModal={() => setActiveLineItemDetails(null)}
                        product={activeLineItemDetails}
                    />
                </Layout>

                <PageActions
                    primaryAction={[
                        {
                            content: 'Place Order and Review',
                            onAction: placeAndReview,
                            loading: creating || uploading || activeLineItem
                        },
                    ]}
                />
            </Page>
        </Frame>
    )
}

export default Orders;
